import TileLayer from 'ol/layer/Tile';
import { get as getProjection } from 'ol/proj';
import OSM from 'ol/source/OSM';
import TileWMS from 'ol/source/TileWMS';
import WMTS from 'ol/source/WMTS';
import XYZ from 'ol/source/XYZ';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import type { AdminBasemap, WmsBasemapSettings, WmtsBasemapSettings } from '../admin/types';

/** Used only when the workspace's basemap catalog is empty or failed to load —
 * a real installation always has at least the seeded OSM/Esri/CARTO entries. */
export function createFallbackBasemapLayer(): TileLayer<OSM> {
  return new TileLayer({ source: new OSM() });
}

/** WMTS matrix set CRS comes as an OGC URN ("urn:ogc:def:crs:EPSG::3857",
 * "urn:ogc:def:crs:OGC:1.3:CRS84") or a plain code ("EPSG:3857") — resolve the
 * trailing identifier to an EPSG code known to OL. Matching this wrong is not
 * cosmetic: origins/resolutions are computed server-side in that CRS's own
 * units (meters for 3857, degrees for 4326/CRS84) — declaring the wrong
 * projection here makes OL divide degree-scale resolutions against
 * meter-scale view coordinates, which can send it computing astronomical tile
 * indices and hang the tab. OL only has EPSG:4326/3857 registered without
 * ol/proj4, which covers the vast majority of real WMTS matrix sets
 * (GoogleMapsCompatible / WGS84 grids). */
function wmtsProjection(crs: string) {
  const suffix = crs.split(':').pop()?.toUpperCase() ?? '';
  const code =
    suffix === '3857' || suffix === '900913'
      ? 'EPSG:3857'
      : suffix === '4326' || suffix === 'CRS84' || suffix === '84'
        ? 'EPSG:4326'
        : /^\d+$/.test(suffix)
          ? `EPSG:${suffix}`
          : 'EPSG:3857';
  return getProjection(code) ?? getProjection('EPSG:3857')!;
}

/** Builds a WMTS tile layer from a server-resolved tile matrix set (see
 * WmtsBasemapSettings) — shared by basemaps (single active background,
 * map/basemaps.ts) and regular toggleable raster layers (MapContext.tsx),
 * since both need the exact same tile grid math. Returns null for malformed
 * settings (e.g. an empty matrix_ids list) so callers can fall back safely. */
export function buildWmtsLayer(settings: WmtsBasemapSettings | undefined, attribution?: string): TileLayer<WMTS> | null {
  if (!settings || settings.matrix_ids.length === 0) return null;

  const tileGrid = new WMTSTileGrid({
    origins: settings.origins,
    resolutions: settings.resolutions,
    matrixIds: settings.matrix_ids,
    tileSize: settings.tile_sizes[0] ?? 256,
  });

  return new TileLayer({
    source: new WMTS({
      url: settings.request_encoding === 'REST' && settings.resource_url ? settings.resource_url : settings.url,
      layer: settings.layer,
      matrixSet: settings.matrix_set,
      format: settings.format,
      style: settings.style,
      projection: wmtsProjection(settings.crs),
      tileGrid,
      requestEncoding: settings.request_encoding === 'REST' ? 'REST' : 'KVP',
      dimensions: settings.dimensions,
      attributions: attribution || undefined,
      crossOrigin: 'anonymous',
    }),
  });
}

/** Builds a live OL tile layer from a catalog entry — "WMTS" via its
 * server-resolved tile matrix set (see WmtsBasemapSettings); "WMS" via a
 * plain GetMap request (see WmsBasemapSettings — url_template is the WMS
 * base URL, not a {z}/{x}/{y} template); "XYZ Tiles", "TMS" and "MBTiles"
 * are all the same "{z}/{x}/{y}" tile-URL-template shape from the browser's
 * point of view (TMS servers just number rows bottom-up, expressed via OL's
 * "{-y}" placeholder in the template itself — no separate source class
 * needed), so they all go through ol/source/XYZ. */
export function buildBasemapLayer(basemap: AdminBasemap): TileLayer<OSM | XYZ | WMTS | TileWMS> {
  if (basemap.sourceType === 'WMTS') {
    return buildWmtsLayer(basemap.settings as WmtsBasemapSettings | undefined, basemap.attribution) ?? createFallbackBasemapLayer();
  }
  if (basemap.sourceType === 'WMS') {
    const wms = basemap.settings as WmsBasemapSettings | undefined;
    if (!basemap.urlTemplate.trim() || !wms?.layer) return createFallbackBasemapLayer();
    return new TileLayer({
      source: new TileWMS({
        url: basemap.urlTemplate,
        params: { LAYERS: wms.layer, VERSION: wms.version || '1.3.0', FORMAT: wms.format || 'image/png', TRANSPARENT: true },
        attributions: basemap.attribution || undefined,
        crossOrigin: 'anonymous',
      }),
    });
  }
  if (!basemap.urlTemplate.trim()) {
    return createFallbackBasemapLayer();
  }
  return new TileLayer({
    source: new XYZ({
      url: basemap.urlTemplate,
      tileSize: basemap.tileSize,
      minZoom: basemap.minZoom,
      maxZoom: basemap.maxZoom,
      attributions: basemap.attribution || undefined,
      crossOrigin: 'anonymous',
    }),
  });
}

export function pickInitialBasemap(basemaps: AdminBasemap[], defaultBasemapName?: string): AdminBasemap | null {
  return (
    basemaps.find((b) => b.enabled && b.name === defaultBasemapName) ??
    basemaps.find((b) => b.enabled && b.isDefault) ??
    basemaps.find((b) => b.enabled) ??
    null
  );
}
